import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { encounterQueryKeys, encountersApi } from "../../shared/api/encounters";
import type { Diagnosis } from "../../shared/types/encounter";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { EncounterShell } from "./components/EncounterShell";
import { Icd10Combobox } from "./components/Icd10Combobox";

type DraftDx = Omit<Diagnosis, "id">;

export function EncounterDiagnosesPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/encounters" replace />;

  return (
    <EncounterShell encounterId={id}>
      {(e) => (
        <DiagnosisEditor
          encounterId={id}
          initial={e.diagnoses}
          readOnly={e.status !== "IN_PROGRESS"}
        />
      )}
    </EncounterShell>
  );
}

function DiagnosisEditor({
  encounterId,
  initial,
  readOnly,
}: {
  encounterId: string;
  initial: Diagnosis[];
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<DraftDx[]>(
    initial.map((d) => ({
      icd10Code: d.icd10Code,
      label: d.label,
      primary: d.primary,
      note: d.note,
    }))
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(
      initial.map((d) => ({
        icd10Code: d.icd10Code,
        label: d.label,
        primary: d.primary,
        note: d.note,
      }))
    );
  }, [initial]);

  const save = useMutation({
    mutationFn: () => encountersApi.replaceDiagnoses(encounterId, draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: encounterQueryKeys.detail(encounterId) });
      setError(null);
    },
    onError: (e) => setError(apiError(e).detail),
  });

  function add(d: DraftDx) {
    setDraft((cur) => {
      const next: DraftDx[] = [...cur, d];
      // First row defaults to primary
      if (next.filter((x) => x.primary).length === 0 && next.length > 0) {
        next[0]!.primary = true;
      }
      return next;
    });
  }

  function update(idx: number, patch: Partial<DraftDx>) {
    setDraft((cur) =>
      cur.map((d, i) => {
        if (i !== idx) {
          // Only one primary allowed
          if (patch.primary === true) return { ...d, primary: false };
          return d;
        }
        return { ...d, ...patch };
      })
    );
  }

  function remove(idx: number) {
    setDraft((cur) => {
      const next = cur.filter((_, i) => i !== idx);
      // Ensure at least one primary if any rows remain
      if (next.length > 0 && next.every((d) => !d.primary)) {
        next[0]!.primary = true;
      }
      return next;
    });
  }

  const dirty =
    JSON.stringify(draft) !==
    JSON.stringify(
      initial.map((d) => ({
        icd10Code: d.icd10Code,
        label: d.label,
        primary: d.primary,
        note: d.note,
      }))
    );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Chẩn đoán</CardTitle>
        {dirty && !readOnly && (
          <Button
            type="button"
            size="sm"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? "Đang lưu…" : "Lưu thay đổi"}
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {!readOnly && (
          <div className="max-w-3xl space-y-1.5">
            <Icd10Combobox
              onPick={(entry) =>
                add({
                  icd10Code: entry.code || null,
                  label: entry.labelVi,
                  primary: false,
                  note: null,
                })
              }
            />
            <p className="text-xs text-muted-foreground">
              Gõ mã ICD-10 (VD:{" "}
              <span className="font-mono">K29.7</span>) hoặc tên bệnh (VD: "viêm
              dạ dày"). Không có trong danh mục? Enter để thêm chẩn đoán tự do.
            </p>
          </div>
        )}

        {draft.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Chưa có chẩn đoán nào. Cần ít nhất 1 chẩn đoán trước khi ký lần khám.
          </p>
        ) : (
          <div className="space-y-2">
            <div className="hidden grid-cols-[7rem_1fr_1fr_5rem_2.5rem] gap-3 px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground md:grid">
              <span>ICD-10</span>
              <span>Chẩn đoán</span>
              <span>Ghi chú</span>
              <span>Chính</span>
              <span></span>
            </div>
            {draft.map((d, i) => (
              <div
                key={i}
                className="grid grid-cols-1 items-center gap-3 rounded-md border bg-background p-3 md:grid-cols-[7rem_1fr_1fr_5rem_2.5rem] md:border-0 md:bg-transparent md:p-0"
              >
                <span className="font-mono text-sm font-medium">
                  {d.icd10Code || (
                    <span className="font-sans font-normal text-muted-foreground">
                      Tự do
                    </span>
                  )}
                </span>
                <Input
                  type="text"
                  value={d.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                  disabled={readOnly}
                />
                <Input
                  type="text"
                  value={d.note ?? ""}
                  onChange={(e) => update(i, { note: e.target.value || null })}
                  disabled={readOnly}
                  placeholder="Ghi chú thêm (tuỳ chọn)"
                />
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="primary-dx"
                    className="h-4 w-4 accent-primary"
                    checked={d.primary}
                    onChange={() => update(i, { primary: true })}
                    disabled={readOnly}
                  />
                  {d.primary ? "Chính" : ""}
                </label>
                {!readOnly ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 justify-self-end text-muted-foreground hover:text-destructive"
                    onClick={() => remove(i)}
                    aria-label="Xoá chẩn đoán"
                  >
                    <X />
                  </Button>
                ) : (
                  <span />
                )}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
